import * as React from 'react';
import { memo, useMemo, useState } from 'react';
import { injectIntl, WrappedComponentProps as InjectedIntlProps } from 'react-intl';
import { Loader } from 'semantic-ui-react';
import { localeData } from '../../../locales/data';
import { FieldDescriptionData, FieldDescriptionResponse } from '../../../shared/api/api';

import { UseQueryResult } from 'react-query';
import { getColumnTransform } from '../DataTableCommon/DataTableCommon';
import { useDataExplorerTrackCustomEvent } from '../Filters/dataExplorerAnalytics';
import { FiltersControl } from '../Filters/types';
import noop from 'lodash/noop';
import { useWatchCommonDataFields } from '../CommonDataFieldsSelector/CommonDataFieldsSelector';
import { addModifiers } from '../../../shared/helpers';
import { FormattedMessageHelper } from '../../../shared/helpers/internationalization';
import { useSingleEntityDataTable } from '../DataTableCommon/useSingleEntityDataTable';
import { getIsFetchNotFoundError } from '../../../shared/api/utils/fetchErrors';
import {
  getCoreRowModel,
  getExpandedRowModel,
  getGroupedRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { getRegionStatsTableColumns } from './RegionTableColumns';
import { RegionTableMarkup } from './RegionTableMarkup';
import { useSelector } from 'react-redux';
import { hasConsultantOrResearcherPermission } from '../../../reducers/AuthenticationReducer/AuthenticationSelector';

type Props = {
  fieldDescriptionResult: UseQueryResult<FieldDescriptionResponse, Error>;
  control: FiltersControl;
} & InjectedIntlProps;

const grouping = ['region'];

const DatasetRegionsTableWithoutIntl = ({ intl, fieldDescriptionResult, control }: Props) => {
  const { isFetching, isError, data: fieldDescription } = fieldDescriptionResult;
  const isPyramidMultiplierVisible = useSelector(hasConsultantOrResearcherPermission);
  const data = fieldDescription?.data;
  const transformColumn = useMemo(
    () =>
      getColumnTransform<FieldDescriptionData>({
        intl,
        sortOrder: { key: 'none', isAscending: true },
        handleOrderChange: noop,
        isOrderDisabled: true,
      }),
    [intl]
  );
  const trackEvent = useDataExplorerTrackCustomEvent();

  const tableColumns = useMemo(() => {
    const columns = getRegionStatsTableColumns<FieldDescriptionData>(trackEvent, isPyramidMultiplierVisible).filter(
      column => column.id !== 'fieldCount'
    );
    return columns.map(transformColumn);
  }, [transformColumn, trackEvent]);
  const selectedFilters = useWatchCommonDataFields(control);

  const {
    getTrProps,
    isTableExpanded,
    expandedRows,
    setExpandedRows,
    activeFieldData,
    toggleTableExpanded,
    dataToRender,
    isExpandVisible,
  } = useSingleEntityDataTable<FieldDescriptionData>(control, data);

  const [sorting, setSorting] = useState<SortingState>([]);

  const table = useReactTable({
    columns: tableColumns,
    data: dataToRender || [],
    state: {
      grouping,
      expanded: expandedRows,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    enableSortingRemoval: false,
    getGroupedRowModel: getGroupedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    manualExpanding: true,
  });

  const headerGroups = table.getHeaderGroups();

  // show loader when data load for the first time
  if (isFetching && !data) {
    return (
      <div className="loader">
        <Loader active={true} inline="centered" size="massive" />
      </div>
    );
  }

  if (isError || !data) {
    const errorMessageId = getIsFetchNotFoundError(fieldDescriptionResult.error)
      ? 'dataExplorer.dataFieldTableError.notFound'
      : 'dataExplorer.dataFieldTableError';

    return (
      <div className="agreements__content full-height">
        <div className="agreements__content-scroll full-height">
          <div className="agreements__not-available full-height">
            <FormattedMessageHelper id={errorMessageId} />
          </div>
        </div>
      </div>
    );
  }

  const toggleButtonTextKey = isTableExpanded ? 'dataExplorer.showLess' : 'dataExplorer.showMore';

  function getExpanderClickHandler(rowId: string) {
    return () => {
      if (rowId in expandedRows) {
        setExpandedRows(prevState => ({ ...expandedRows, [rowId]: !prevState[rowId] }));
      } else {
        setExpandedRows(prevState => ({ ...expandedRows, [rowId]: true }));
      }
    };
  }

  return (
    <div className={addModifiers('data-table__container data-single-entity-table', [isTableExpanded ? 'expanded' : ''])}>
      {!activeFieldData && (
        <div className={'data-single-entity__not-found-note'}>
          <FormattedMessageHelper
            tagName="span"
            id={'dataExplorer.fieldDataNotFound'}
            defaultMessage={localeData.en['dataExplorer.fieldDataNotFound']}
            values={{ region: selectedFilters.region, delay: selectedFilters.delay, universe: selectedFilters.universe }}
          />
        </div>
      )}
      <RegionTableMarkup<FieldDescriptionData>
        data={data}
        isFetching={isFetching}
        headerGroups={headerGroups}
        sorting={sorting}
        table={table}
        getTrProps={getTrProps}
        isTableExpanded={isTableExpanded}
        getExpanderClickHandler={getExpanderClickHandler}
        isExpandVisible={isExpandVisible}
        toggleTableExpanded={toggleTableExpanded}
        toggleButtonTextKey={toggleButtonTextKey}
      />
    </div>
  );
};

export const DataFieldRegionsTable = injectIntl(memo(DatasetRegionsTableWithoutIntl));
